import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Loading,
  MessagePlugin,
  Tag,
  Textarea,
  Upload,
  type UploadFile,
  type UploadProps,
} from 'tdesign-react';
import { FileIcon } from 'tdesign-icons-react';
import {
  DISPATCH_STATUS_LABEL,
  DISPATCH_STATUS_THEME,
  DispatchStatus,
  SUB_CATEGORY_LABEL,
  fetchCompleteInfo,
  submitComplete,
  uploadCompleteFile,
  type CompleteInfo,
  type UploadedFileMeta,
} from '@/services/command';
import { formatDateTime, formatDateOnly } from '@/utils/datetime';
import './CompletePage.css';

const ALLOWED_EXTS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.zip',
  '.rar',
  '.csv',
  '.txt',
  '.png',
  '.jpg',
  '.jpeg',
];
const MAX_FILE_SIZE = 50 * 1024 * 1024;

const formatSize = (bytes: number | null | undefined) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * 「指令完成」独立页面
 *
 * URL：`/portal/complete?commandId=<id>&token=<t>`
 *
 * 鉴权口径（双闸门）：
 *  1) 智能网关 x-tai-identity：企微客户端跳内网 portal 时注入，服务端解密后
 *     `/api/tai/*` 独立拒绝层（TaiIdentityRequiredMiddleware）强制要求身份，
 *     无 Tai 身份直接 401（无 ADMIN_USERS 白名单校验）。
 *  2) URL token（HMAC 签名，不校验过期时间）：由 worker 侧 signCompleteToken 签发，
 *     server 侧 /tai/complete-info 与 /tai/complete 均校验；签名不通过 → 401。
 *     完成页链接**永久有效**，无"过期"概念（仅在 token 伪造 / 密钥更换后才失效）。
 *
 * 页面本身不受 PortalGuard 微信登录保护。
 *
 * 需回复类指令必须提交「回执附件」或「完成说明文本」中的至少一项（二选一必填）后才能提交完成；
 * 不需回复类指令按理由 worker 直接完成，用户不会被引导到本页；但如果误进入，仍允许"无附件确认完成"作为兜底路径。
 *
 * 页面状态机（本地）：
 *   - loading  → 首屏拉 complete-info
 *   - error    → 拉不到指令 / commandId 非法 / token 无效（签名不通过） / 已删除
 *   - ready    → 展示指令详情 + 上传区
 *   - done     → 已完成（首屏进入时状态已是 Completed，或本次提交成功）
 */
const CompletePage = () => {
  const [params] = useSearchParams();
  const commandId = Number(params.get('commandId') || 0);
  const token = params.get('token') || '';

  const paramsOk = commandId > 0 && !!token;

  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'done'>('loading');
  const [errMsg, setErrMsg] = useState<string>('');
  const [info, setInfo] = useState<CompleteInfo | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploaded, setUploaded] = useState<UploadedFileMeta | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 完成说明文本（与回执附件二选一必填，need_reply=1 场景）
  const [completeText, setCompleteText] = useState('');
  const COMPLETE_TEXT_MAX = 2000;

  const loadInfo = useCallback(async () => {
    if (!paramsOk) {
      setStatus('error');
      setErrMsg('链接参数错误，请重新从企微卡片进入');
      return;
    }
    setStatus('loading');
    try {
      const data = await fetchCompleteInfo(commandId, token);
      setInfo(data);
      // PendingConfirm（已完成待确认）在处理人视角等同于「已完成」：
      // 处理人已经提交过完成回执，剩下只是管理员在后台点「确认完成」。
      // 因此完成页把它也视为 done，避免误报 "暂不允许完成"。
      if (
        data.dispatchStatus === DispatchStatus.Completed ||
        data.dispatchStatus === DispatchStatus.PendingConfirm
      ) {
        setStatus('done');
      } else if (data.dispatchStatus !== DispatchStatus.Dispatched) {
        setErrMsg(
          `指令当前状态为「${DISPATCH_STATUS_LABEL[data.dispatchStatus] ?? data.dispatchStatus}」，暂不允许完成`,
        );
        setStatus('error');
      } else {
        // 若指令带有历史「完成回执附件 / 完成说明」，说明此前已提交过一次但被管理员
        // 「退回」到 Dispatched。这时把历史内容预填到表单，处理人可参照修改后再次提交，
        // 提交时后端 `commandRepo.update(...)` 会用新值直接覆盖旧字段。
        if (data.completeFileKey && data.completeFileName) {
          setUploaded({
            fileKey: data.completeFileKey,
            fileName: data.completeFileName,
            fileSize: data.completeFileSize ?? 0,
          });
          setFileList([
            {
              name: data.completeFileName,
              size: data.completeFileSize ?? 0,
              status: 'success',
              percent: 100,
              url: data.completeFileDownloadUrl || undefined,
              response: {
                url: data.completeFileKey,
                fileKey: data.completeFileKey,
                fileName: data.completeFileName,
                fileSize: data.completeFileSize ?? 0,
              },
            },
          ]);
        } else {
          setUploaded(null);
          setFileList([]);
        }
        setCompleteText(data.completeText ?? '');
        setStatus('ready');
      }
    } catch (err) {
      // silent 请求：错误不弹全局 toast，这里显示到页面上
      setErrMsg((err as Error).message || '指令不存在或链接已失效');
      setStatus('error');
    }
  }, [commandId, token, paramsOk]);

  useEffect(() => {
    void loadInfo();
  }, [loadInfo]);

  const validateFile = (raw: File): boolean => {
    const name = raw.name || '';
    const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
    if (!ALLOWED_EXTS.includes(ext)) {
      MessagePlugin.error(`不支持的文件类型（允许：${ALLOWED_EXTS.join(', ')}）`);
      return false;
    }
    if (raw.size > MAX_FILE_SIZE) {
      MessagePlugin.error('文件大小不能超过 50MB');
      return false;
    }
    return true;
  };

  const startUpload = async (file: UploadFile, raw: File) => {
    setUploading(true);
    setUploaded(null);
    setFileList([{ ...file, status: 'progress', percent: 0 }]);
    try {
      const meta = await uploadCompleteFile(raw, (percent) => {
        setFileList((prev) =>
          prev.length > 0 ? [{ ...prev[0], status: 'progress', percent }] : prev,
        );
      });
      setUploaded(meta);
      setFileList([
        { ...file, status: 'success', percent: 100, response: { url: meta.fileKey, ...meta } },
      ]);
      MessagePlugin.success('回执附件上传成功');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '文件上传失败';
      setFileList([{ ...file, status: 'fail', percent: 0 }]);
      setUploaded(null);
      MessagePlugin.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange: UploadProps['onChange'] = (files) => {
    if (!files || files.length === 0) {
      setFileList([]);
      setUploaded(null);
      return;
    }
    const file = files[files.length - 1];
    const raw = file.raw as File | undefined;
    if (!raw) {
      setFileList(files);
      return;
    }
    // 已上传成功的文件不重复触发
    if (uploaded && file.status === 'success') {
      setFileList(files);
      return;
    }
    if (!validateFile(raw)) {
      setFileList([]);
      setUploaded(null);
      return;
    }
    void startUpload(file, raw);
  };

  const needReply = info?.needReply === 1;

  const trimmedText = completeText.trim();
  // 需回复场景下按钮的可用条件：附件 or 文本 至少存在其一
  const canSubmit = needReply ? !!uploaded || trimmedText.length > 0 : true;

  const handleSubmit = async () => {
    if (!info) return;
    if (uploading) {
      MessagePlugin.warning('文件上传中，请稍候');
      return;
    }
    if (needReply && !uploaded && !trimmedText) {
      MessagePlugin.error('该指令需反馈，请上传完成回执附件或填写完成说明（二选一）');
      return;
    }
    setSubmitting(true);
    try {
      await submitComplete({
        commandId,
        token,
        completeFileKey: uploaded?.fileKey,
        completeFileName: uploaded?.fileName,
        completeFileSize: uploaded?.fileSize,
        completeText: trimmedText || undefined,
      });
      MessagePlugin.success('完成提交成功');
      setStatus('done');
      // 拉一次最新详情让页面显示完成人 / 完成时间
      void loadInfo();
    } catch (err) {
      MessagePlugin.error((err as Error).message || '提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const detailRows = useMemo(() => {
    if (!info) return [] as { label: string; value: React.ReactNode }[];
    // 「指令内容」/「原附件」按需展示（业务保证至少存在其一）
    const rows: { label: string; value: React.ReactNode }[] = [
      { label: '指令 ID', value: `#${info.id}` },
      {
        label: '指令分类',
        value: SUB_CATEGORY_LABEL[info.subCategory] ?? info.subCategory,
      },
      // 前台完成页只展示「下发单位」，不展示「下发人」姓名/工号。
      // 「下发人」属于后台运营视角字段，仅在 /commands 后台列表出现。
      {
        label: '下发单位',
        value: info.submitterOrganization || '-',
      },
      { label: '下发时间', value: formatDateTime(info.createdAt) },
      {
        label: '是否需反馈',
        value: needReply ? (
          <Tag theme="warning" variant="light">
            需反馈
          </Tag>
        ) : (
          <Tag variant="light">无需反馈</Tag>
        ),
      },
      {
        label: '反馈日期',
        // 走 formatDateOnly 与「下发时间」保持相同风格（YYYY/M/D）。
        value: needReply && info.deadlineDate ? formatDateOnly(info.deadlineDate) : '-',
      },
      {
        label: '状态',
        value: (
          <Tag
            theme={DISPATCH_STATUS_THEME[info.dispatchStatus] ?? 'default'}
            variant="light"
          >
            {DISPATCH_STATUS_LABEL[info.dispatchStatus] ?? info.dispatchStatus}
          </Tag>
        ),
      },
    ];

    if (info.content) {
      rows.push({
        label: '指令内容',
        value: <div className="complete-content-text">{info.content}</div>,
      });
    }
    if (info.fileName) {
      rows.push({
        label: '原附件',
        value: (
          <a
            className="complete-file-link"
            href={info.fileDownloadUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FileIcon />
            <span>{info.fileName}</span>
            <span className="complete-file-size">· {formatSize(info.fileSize)}</span>
          </a>
        ),
      });
    }
    return rows;
  }, [info, needReply]);

  return (
    <div className="complete-page">
      <div className="complete-container">
        <div className="complete-header">
          <div className="complete-title">📋 指令完成</div>
          <div className="complete-subtitle">
            请核对指令信息{needReply ? '并提交完成回执附件或完成说明（二选一）' : ''}
          </div>
        </div>

        {status === 'loading' ? (
          <div className="complete-loading">
            <Loading text="加载中..." />
          </div>
        ) : null}

        {status === 'error' ? (
          <div className="complete-error">
            <div className="complete-error-icon">⚠️</div>
            <div className="complete-error-msg">{errMsg}</div>
          </div>
        ) : null}

        {info && (status === 'ready' || status === 'done') ? (
          <>
            <ul className="complete-detail">
              {detailRows.map((row) => (
                <li key={row.label} className="complete-detail-row">
                  <span className="complete-detail-label">{row.label}</span>
                  <span className="complete-detail-value">{row.value}</span>
                </li>
              ))}
              {status === 'done' && info.completedAt ? (
                <>
                  <li className="complete-detail-row">
                    <span className="complete-detail-label">完成时间</span>
                    <span className="complete-detail-value">
                      {formatDateTime(info.completedAt)}
                    </span>
                  </li>
                  <li className="complete-detail-row">
                    <span className="complete-detail-label">完成人</span>
                    <span className="complete-detail-value">
                      {info.completedBy || '-'}
                    </span>
                  </li>
                  {info.completeFileName ? (
                    <li className="complete-detail-row">
                      <span className="complete-detail-label">完成回执</span>
                      <span className="complete-detail-value">{info.completeFileName}</span>
                    </li>
                  ) : null}
                  {info.completeText ? (
                    <li className="complete-detail-row">
                      <span className="complete-detail-label">完成说明</span>
                      <span className="complete-detail-value">
                        <div className="complete-content-text">{info.completeText}</div>
                      </span>
                    </li>
                  ) : null}
                </>
              ) : null}
            </ul>

            {status === 'ready' ? (
              <div className="complete-form">
                {needReply ? (
                  <>
                    <div className="complete-tips">
                      「完成回执附件」与「完成说明」二选一必填，也可同时提交。
                    </div>
                    <div className="complete-section-title">完成回执附件</div>
                    <Upload
                      theme="file"
                      draggable
                      autoUpload={false}
                      max={1}
                      accept={ALLOWED_EXTS.join(',')}
                      files={fileList}
                      tips={`支持 ${ALLOWED_EXTS.join('、')}，单文件 ≤ 50MB`}
                      onChange={handleFileChange}
                    />
                    <div
                      className="complete-section-title"
                      style={{ marginTop: 16 }}
                    >
                      完成说明
                    </div>
                    <Textarea
                      value={completeText}
                      onChange={(v) => setCompleteText(v ?? '')}
                      placeholder="请填写完成情况说明（如：现场处置已闭环 / 无需处置的原因 …）"
                      autosize={{ minRows: 3, maxRows: 6 }}
                      maxlength={COMPLETE_TEXT_MAX}
                    />
                  </>
                ) : (
                  <div className="complete-tips">
                    该指令无需上传回执附件，点击下方按钮直接确认完成。
                  </div>
                )}
                <div className="complete-submit-row">
                  <Button
                    theme="primary"
                    size="large"
                    loading={submitting || uploading}
                    disabled={submitting || uploading || !canSubmit}
                    onClick={handleSubmit}
                  >
                    确认已完成
                  </Button>
                </div>
              </div>
            ) : (
              <div className="complete-done">
                <div className="complete-done-icon">✅</div>
                <div className="complete-done-msg">该指令已完成，可以关闭本页。</div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CompletePage;
